<template>
  <div>
    <AllegroAccountHeader
      :accountPk="route.params.pk"
      :accountData="accountData"
      activeTab="settings"
    >
      <div class="row">
        <div
          class="col-lg-12"
          v-for="section in visibleSections"
          :key="section.key"
        >
          <div class="ibox">
            <div class="ibox-title ibox-title-flex">
              <h5>
                {{ section.title }}
                <span class="allegro-help-popover">
                  <i class="fa fa-question-circle text-muted"></i>
                  <span class="allegro-help-popover-content">
                    {{ section.helpText }}
                  </span>
                </span>
              </h5>
              <div class="ibox-title-operations" v-if="!section.loading">
                <router-link
                  v-if="!section.data?.pk && section.canAdd"
                  :to="section.createRoute"
                  class="btn btn-primary"
                >
                  <i class="fa fa-plus"></i> Dodaj
                </router-link>
                <router-link
                  v-else-if="section.data?.pk && section.canUpdate"
                  :to="section.updateRoute(section.data.pk)"
                  class="btn btn-primary"
                >
                  <i class="fa fa-edit"></i> Edytuj
                </router-link>
              </div>
            </div>
            <div class="ibox-content">
              <div v-if="section.loading" class="sk-spinner sk-spinner-wave">
                <div class="sk-rect1"></div>
                <div class="sk-rect2"></div>
                <div class="sk-rect3"></div>
                <div class="sk-rect4"></div>
                <div class="sk-rect5"></div>
              </div>
              <div
                v-else-if="!section.data || !section.data.pk"
                class="alert alert-warning"
              >
                <i class="fa fa-warning"></i>
                {{ section.alertText }}
              </div>
              <div v-else class="row">
                <div class="col-md-6">
                  <dl class="dl-horizontal">
                    <template v-for="field in section.fields" :key="field.key">
                      <dt>{{ field.label }}:</dt>
                      <dd>
                        {{
                          field.value
                            ? field.value(section.data)
                            : getNestedValue(section.data, field.key)
                        }}
                      </dd>
                    </template>
                  </dl>
                </div>
                <div class="col-md-6">
                  <dl class="dl-horizontal">
                    <dt>Utworzył:</dt>
                    <dd>{{ section.data.created_by || "-" }}</dd>
                    <dt>Data utworzenia:</dt>
                    <dd>{{ section.data.date_added || "-" }}</dd>
                    <br />
                    <dt>Zmodyfikował:</dt>
                    <dd>{{ section.data.last_modified_by || "-" }}</dd>
                    <dt>Data modyfikacji:</dt>
                    <dd>{{ section.data.date_modified || "-" }}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </AllegroAccountHeader>
  </div>
</template>

<script>
import { computed, ref, watch } from "vue";
import { useStore } from "vuex";
import { useRoute } from "vue-router";
import AllegroAccountHeader from "./AllegroAccountHeader.vue";
import { hasPermission } from "../../permissions";
import {
  allegroConfigPermissionsConst,
  allegroSellerPermissionsConst,
  allegroSenderPermissionsConst,
  allegroCodReceiverPermissionsConst,
  allegroResponsibleProducerPermissionsConst,
  allegroSafetyInformationPermissionsConst,
} from "../../constants/allegroConstants";

export default {
  name: "AllegroAccountSettings",
  components: { AllegroAccountHeader },
  setup() {
    const route = useRoute();
    const store = useStore();
    const accountData = ref({});
    const oauthData = ref({});
    const sellerData = ref({});
    const senderData = ref({});
    const codReceiverData = ref({});
    const producerData = ref({});
    const safetyData = ref({});

    // Every section loads independently, so each one shows its own spinner
    // until its request comes back - as in the old admin panel.
    const sectionLoading = ref({
      oauth: true,
      seller: true,
      sender: true,
      cod: true,
      responsible: true,
      safety: true,
    });

    const oauthRaw = computed(() => store.getters.getAllegroAccountOauthData);
    const sellerRaw = computed(() => store.getters.getAllegroAccountSellerData);
    const senderRaw = computed(() => store.getters.getAllegroAccountSenderData);
    const codReceiverRaw = computed(
      () => store.getters.getAllegroAccountCodReceiverData
    );
    const producerRaw = computed(
      () => store.getters.getAllegroAccountResponsibleProducerData
    );
    const safetyRaw = computed(
      () => store.getters.getAllegroAccountSafetyInformationData
    );
    const accountRaw = computed(() => store.getters.getAllegroAccountData);

    store.dispatch("getAllegroAccount", route.params.pk);
    store.dispatch("getAllegroAccountOauthData", route.params.pk);
    store.dispatch("getAllegroAccountSellerData", route.params.pk);
    store.dispatch("getAllegroAccountSenderData", route.params.pk);
    store.dispatch("getAllegroAccountCodReceiverData", route.params.pk);
    store.dispatch("getAllegroAccountResponsibleProducerData", route.params.pk);
    store.dispatch("getAllegroAccountSafetyInformationData", route.params.pk);

    const bindSection = (source, target, loadingKey) => {
      watch(
        () => source.value,
        (v) => {
          target.value = v?.data || {};
          sectionLoading.value[loadingKey] = false;
        },
        { deep: true }
      );
    };

    bindSection(oauthRaw, oauthData, "oauth");
    bindSection(sellerRaw, sellerData, "seller");
    bindSection(senderRaw, senderData, "sender");
    bindSection(codReceiverRaw, codReceiverData, "cod");
    bindSection(producerRaw, producerData, "responsible");
    bindSection(safetyRaw, safetyData, "safety");
    watch(
      () => accountRaw.value,
      (v) => (accountData.value = v?.data || {}),
      { deep: true }
    );

    const sections = computed(() => [
      {
        key: "oauth",
        canView: hasPermission(allegroConfigPermissionsConst.view),
        canAdd: hasPermission(allegroConfigPermissionsConst.add),
        canUpdate: hasPermission(allegroConfigPermissionsConst.update),
        loading: sectionLoading.value.oauth,
        title: "Uwierzytelnianie i autoryzacja OAuth",
        data: oauthData.value,
        createRoute: `/allegro/accounts/${route.params.pk}/oauth/create`,
        updateRoute: (pk) => `/allegro/oauth/${pk}/update`,
        fields: [
          { key: "user_agent.value", label: "User-Agent" },
          { key: "client_id", label: "Klient ID" },
          { key: "client_secret", label: "Secret" },
          { key: "base_url", label: "Adres do autoryzacji" },
          { key: "base_api_url", label: "Adres do API" },
        ],
        alertText:
          'Brak skonfigurowanego OAuth. Kliknij przycisk "Dodaj" aby uzupełnić dane.',
        helpText:
          "Dane uwierzytelniające OAuth wymagane do autoryzacji połączenia z API Allegro. Bez poprawnej konfiguracji OAuth nie będzie możliwa synchronizacja ofert, zamówień i innych procesów integracyjnych.",
      },
      {
        key: "seller",
        canView: hasPermission(allegroSellerPermissionsConst.view),
        canAdd: hasPermission(allegroSellerPermissionsConst.add),
        canUpdate: hasPermission(allegroSellerPermissionsConst.update),
        loading: sectionLoading.value.seller,
        title: "Dane sprzedawcy",
        data: sellerData.value,
        createRoute: `/allegro/accounts/${route.params.pk}/seller/create`,
        updateRoute: (pk) => `/allegro/sellers/${pk}/update`,
        fields: [
          { key: "tax_id", label: "NIP" },
          { key: "company_name", label: "Nazwa firmy" },
          {
            key: "full_name",
            label: "Imię i Nazwisko",
            value: (data) =>
              `${data.first_name || "-"} ${data.last_name || "-"}`.trim(),
          },
          { key: "street", label: "Ulica" },
          { key: "zip_code", label: "Kod pocztowy" },
          { key: "city", label: "Miejscowość" },
        ],
        alertText:
          'Brak danych sprzedawcy. Kliknij przycisk "Dodaj" aby uzupełnić dane.',
        helpText:
          "Informacje o sprzedawcy potrzebne są do wystawienia faktury dla zamówień Allegro.",
      },
      {
        key: "sender",
        canView: hasPermission(allegroSenderPermissionsConst.view),
        canAdd: hasPermission(allegroSenderPermissionsConst.add),
        canUpdate: hasPermission(allegroSenderPermissionsConst.update),
        loading: sectionLoading.value.sender,
        title: "Dane nadawcy",
        data: senderData.value,
        createRoute: `/allegro/accounts/${route.params.pk}/sender/create`,
        updateRoute: (pk) => `/allegro/senders/${pk}/update`,
        fields: [
          { key: "company_name", label: "Nazwa firmy" },
          { key: "name", label: "Imię i Nazwisko" },
          { key: "street", label: "Ulica" },
          { key: "zip_code", label: "Kod pocztowy" },
          { key: "city", label: "Miejscowość" },
          { key: "country_code", label: "Kod kraju" },
          { key: "email", label: "E-mail" },
          { key: "phone", label: "Nr telefonu" },
        ],
        alertText:
          'Brak danych nadawcy. Kliknij przycisk "Dodaj" aby uzupełnić dane.',
        helpText:
          "Dane adresowe nadawcy wykorzystywane są przy generowaniu etykiet przesyłek. Te informacje są wymagane przez firmy kurierskie do prawidłowego nadania paczek.",
      },
      {
        key: "cod",
        canView: hasPermission(allegroCodReceiverPermissionsConst.view),
        canAdd: hasPermission(allegroCodReceiverPermissionsConst.add),
        canUpdate: hasPermission(allegroCodReceiverPermissionsConst.update),
        loading: sectionLoading.value.cod,
        title: "Dane odbiorcy przelewu",
        data: codReceiverData.value,
        createRoute: `/allegro/accounts/${route.params.pk}/cod-receiver/create`,
        updateRoute: (pk) => `/allegro/cod-receivers/${pk}/update`,
        fields: [
          { key: "name", label: "Nazwa" },
          { key: "iban", label: "Nr konta (IBAN)" },
        ],
        alertText:
          'Brak danych odbiorcy przelewu. Kliknij przycisk "Dodaj" aby uzupełnić dane.',
        helpText:
          "Dane odbiorcy przelewu za przesyłki pobraniowe są wymagane przez firmy kurierskie do prawidłowego przesłania środków na konto sprzedawcy.",
      },
      {
        key: "responsible",
        canView: hasPermission(allegroResponsibleProducerPermissionsConst.view),
        canAdd: hasPermission(allegroResponsibleProducerPermissionsConst.add),
        canUpdate: hasPermission(
          allegroResponsibleProducerPermissionsConst.update
        ),
        loading: sectionLoading.value.responsible,
        title: "Dane osoby odpowiedzialnej za zgodność produktu",
        data: producerData.value,
        createRoute: `/allegro/accounts/${route.params.pk}/responsible-producer/create`,
        updateRoute: (pk) => `/allegro/responsible-producers/${pk}/update`,
        fields: [{ key: "name", label: "Nazwa własna" }],
        alertText:
          'Brak danych osoby odpowiedzialnej za zgodność produktu. Kliknij przycisk "Dodaj" aby uzupełnić dane.',
        helpText:
          "Zgodnie z rozporządzeniami GPSR oraz DSA wydanymi przez Unię Europejską dodaj osobę odpowiedzialną za określone obowiązki dotyczące zgodności produktu z przepisami.",
      },
      {
        key: "safety",
        canView: hasPermission(allegroSafetyInformationPermissionsConst.view),
        canAdd: hasPermission(allegroSafetyInformationPermissionsConst.add),
        canUpdate: hasPermission(
          allegroSafetyInformationPermissionsConst.update
        ),
        loading: sectionLoading.value.safety,
        title: "Informacje o bezpieczeństwie produktu",
        data: safetyData.value,
        createRoute: `/allegro/accounts/${route.params.pk}/safety-information/create`,
        updateRoute: (pk) =>
          `/allegro/product-safety-informations/${pk}/update`,
        fields: [{ key: "text", label: "Opis" }],
        alertText:
          'Brak danych informacji o bezpieczeństwie produktu. Kliknij przycisk "Dodaj" aby uzupełnić dane.',
        helpText:
          "Celem GPSR jest pokazanie kupującemu, skąd pochodzi produkt, który może kupić na Allegro. Jednym z jego elementów są informacje o bezpieczeństwie, np. instrukcja obsługi.",
      },
    ]);

    // A section the user cannot read is hidden entirely, as in the old panel
    const visibleSections = computed(() =>
      sections.value.filter((section) => section.canView)
    );

    const getNestedValue = (obj, path) => {
      return path.split(".").reduce((acc, key) => acc?.[key], obj) ?? "-";
    };

    return {
      route,
      accountData,
      oauthData,
      sellerData,
      senderData,
      codReceiverData,
      producerData,
      safetyData,
      sections,
      visibleSections,
      getNestedValue,
    };
  },
};
</script>

<style scoped>
.ibox-title-flex {
  align-items: center;
  display: flex;
  justify-content: space-between;
}

.ibox-title-flex h5 {
  margin: 0;
}

.ibox-title-operations {
  margin-left: auto;
}

.allegro-help-popover {
  display: inline-block;
  margin-left: 6px;
  position: relative;
}

.allegro-help-popover-content {
  background-color: #fff;
  border: 1px solid #e7eaec;
  border-radius: 3px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  color: #676a6c;
  display: none;
  font-size: 13px;
  font-weight: 400;
  left: 18px;
  line-height: 1.4;
  min-width: 260px;
  padding: 9px 12px;
  position: absolute;
  top: -8px;
  white-space: normal;
  z-index: 2050;
}

.allegro-help-popover:hover .allegro-help-popover-content {
  display: block;
}
</style>
